import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileFetchedFor = useRef(null);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      profileFetchedFor.current = null;
      return;
    }
    // Skip if we've already fetched the profile for this user
    if (profileFetchedFor.current === userId) return;
    profileFetchedFor.current = userId;
    try {
      const { data } = await supabase
        .from("profiles")
        .select("id, email, is_admin")
        .eq("id", userId)
        .maybeSingle();
      setProfile(data || { id: userId, email: null, is_admin: false });
    } catch (_) {
      setProfile({ id: userId, email: null, is_admin: false });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session || null);
      if (data.session?.user) {
        await fetchProfile(data.session.user.id);
      }
      setLoading(false);
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Ignore token refresh events — they don't change identity
      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        setSession(newSession);
        return;
      }
      setSession(newSession);
      if (newSession?.user) {
        // fetchProfile is idempotent per-user; safe to call here
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
        profileFetchedFor.current = null;
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Eagerly populate session + profile so consumers see auth state immediately
    // (don't wait for the onAuthStateChange round-trip)
    if (data?.session) {
      setSession(data.session);
      if (data.session.user) {
        await fetchProfile(data.session.user.id);
      }
    }
  };

  const signOut = async () => {
    // Clear local state synchronously so consumers redirect immediately
    setSession(null);
    setProfile(null);
    profileFetchedFor.current = null;
    try {
      await supabase.auth.signOut();
    } catch (_) {
      // ignore — local state already cleared
    }
  };

  const value = {
    session,
    user: session?.user || null,
    profile,
    isAuthenticated: !!session?.user,
    isAdmin: !!profile?.is_admin,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
